import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Link2, Unlink, Youtube, Twitter, Instagram, Music2, CheckCircle, AlertCircle, Shield, BarChart3, Eye, Users, FolderOpen, Plus, Facebook, Camera } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'youtube', name: 'YouTube', icon: Youtube, color: '#FF0000', placeholder: '@yourchannel' },
  { id: 'tiktok', name: 'TikTok', icon: Music2, color: '#00F0FF', placeholder: '@yourhandle' },
  { id: 'instagram', name: 'Instagram', icon: Instagram, color: '#E4405F', placeholder: '@yourhandle' },
  { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: '#1DA1F2', placeholder: '@yourhandle' },
  { id: 'facebook', name: 'Facebook', icon: Facebook, color: '#1877F2', placeholder: 'Page URL' },
  { id: 'snapchat', name: 'Snapchat', icon: Camera, color: '#FFFC00', placeholder: '@yoursnapcode' },
  { id: 'soundcloud', name: 'SoundCloud', icon: Music2, color: '#FF5500', placeholder: 'soundcloud.com/you' },
];

export default function VaultPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [vaultStats, setVaultStats] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [handleInput, setHandleInput] = useState('');
  const [showConnect, setShowConnect] = useState(null);

  useEffect(() => {
    if (!user) navigate('/auth');
  }, [user, navigate]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [accountsRes, statsRes] = await Promise.all([
        axios.get(`${API}/accounts/connected`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vault/stats`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setAccounts(accountsRes.data.accounts);
      setVaultStats(statsRes.data);
    } catch (err) { console.error(err); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleConnect = async (platformId) => {
    if (!handleInput.trim()) return;
    setConnecting(platformId);
    try {
      await axios.post(`${API}/accounts/connect`, {
        platform: platformId,
        handle: handleInput.trim(),
        display_name: handleInput.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      setHandleInput('');
      setShowConnect(null);
      fetchData();
    } catch (err) {
      console.error(err);
    }
    setConnecting(null);
  };

  const handleDisconnect = async (accountId) => {
    try {
      await axios.delete(`${API}/accounts/${accountId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const connectedPlatformIds = accounts.map(a => a.platform);

  const stats = vaultStats ? [
    { label: 'Connected Platforms', value: vaultStats.connected_platforms, icon: Link2, color: 'cyan' },
    { label: 'Content Indexed', value: vaultStats.total_content_indexed.toLocaleString(), icon: FolderOpen, color: 'magenta' },
    { label: 'Total Followers', value: vaultStats.total_followers.toLocaleString(), icon: Users, color: 'purple' },
    { label: 'Monitoring Status', value: vaultStats.monitoring_status === 'active' ? 'Active' : 'Inactive', icon: Shield, color: vaultStats.monitoring_status === 'active' ? 'cyan' : 'magenta' },
  ] : [];

  return (
    <div className="min-h-screen bg-texture" data-testid="vault-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-black"  data-testid="vault-title">
            <span className="text-white">QUADRA </span><span className="neon-cyan">Vault</span>
          </h1>
          <p className="text-sm text-[--ch-text-secondary] font-normal mt-1">Connect your platforms. Index your content. SMITHERS monitors everything.</p>
        </div>

        {/* Stats */}
        {vaultStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="vault-stats">
            {stats.map((s, i) => (
              <div key={i} className="ch-card p-4" data-testid={`vault-stat-${i}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${
                  s.color === 'cyan' ? 'bg-[rgba(0,240,255,0.1)] text-[--ch-cyan]' :
                  s.color === 'magenta' ? 'bg-[rgba(255,0,229,0.1)] text-[--ch-magenta]' :
                  'bg-[rgba(139,92,246,0.1)] text-[--ch-purple]'
                }`}>
                  <s.icon size={16} />
                </div>
                <p className="text-xs text-[--ch-text-secondary]">{s.label}</p>
                <p className="text-lg font-black mt-0.5">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Platform Connections */}
        <div className="mb-8">
          <h2 className="text-sm font-bold mb-4 flex items-center gap-2" >
            <Link2 size={16} className="text-[--ch-cyan]" /> Connected Platforms
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PLATFORMS.map((platform) => {
              const isConnected = connectedPlatformIds.includes(platform.id);
              const account = accounts.find(a => a.platform === platform.id);
              const Icon = platform.icon;

              return (
                <div key={platform.id} className={`ch-card p-4 transition-all ${isConnected ? 'border-[rgba(34,197,94,0.3)]' : ''}`} data-testid={`platform-${platform.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${platform.color}15` }}>
                      <Icon size={20} style={{ color: platform.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">{platform.name}</p>
                        {isConnected && (
                          <div className="flex items-center gap-1">
                            <div className="w-1.5 h-1.5 rounded-full bg-[--ch-success] shadow-[0_0_4px_rgba(34,197,94,0.6)]" />
                            <span className="text-xs text-[--ch-success] font-bold">Connected</span>
                          </div>
                        )}
                      </div>
                      {isConnected && account && (
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs text-[--ch-text-secondary]">{account.handle}</span>
                          <span className="text-xs text-[--ch-text-secondary]">{account.followers?.toLocaleString()} followers</span>
                          <span className="text-xs text-[--ch-text-secondary]">{account.content_count} items</span>
                        </div>
                      )}
                    </div>
                    {isConnected ? (
                      <button
                        data-testid={`disconnect-${platform.id}`}
                        onClick={() => handleDisconnect(account.id)}
                        className="text-xs text-[--ch-text-secondary] hover:text-[--ch-error] flex items-center gap-1 transition-colors"
                      >
                        <Unlink size={12} /> Disconnect
                      </button>
                    ) : (
                      <button
                        data-testid={`connect-${platform.id}`}
                        onClick={() => { setShowConnect(platform.id); setHandleInput(''); }}
                        className="text-xs text-[--ch-cyan] hover:text-white flex items-center gap-1 transition-colors font-semibold"
                      >
                        <Plus size={12} /> Connect
                      </button>
                    )}
                  </div>

                  {/* Connect form */}
                  {showConnect === platform.id && !isConnected && (
                    <div className="mt-3 pt-3 border-t border-[--ch-border] flex gap-2" data-testid={`connect-form-${platform.id}`}>
                      <input
                        type="text"
                        value={handleInput}
                        onChange={e => setHandleInput(e.target.value)}
                        placeholder={platform.placeholder}
                        className="flex-1 bg-[--ch-bg-secondary] border border-[--ch-border] rounded-lg px-3 py-2 text-sm text-white focus:border-[--ch-cyan] focus:outline-none"
                        data-testid={`handle-input-${platform.id}`}
                      />
                      <button
                        onClick={() => handleConnect(platform.id)}
                        disabled={connecting === platform.id || !handleInput.trim()}
                        className="btn-neon px-4 py-2 rounded-lg text-xs disabled:opacity-50"
                        data-testid={`confirm-connect-${platform.id}`}
                      >
                        {connecting === platform.id ? '...' : 'Connect'}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Connected accounts detail */}
        {accounts.length > 0 && (
          <div className="ch-card overflow-hidden" data-testid="indexed-content">
            <div className="p-4 border-b border-[--ch-border]">
              <h3 className="text-sm font-bold flex items-center gap-2" >
                <FolderOpen size={14} className="text-[--ch-magenta]" /> Indexed Content
              </h3>
            </div>
            <div className="divide-y divide-[--ch-border]">
              {accounts.map((account) => {
                const platformDef = PLATFORMS.find(p => p.id === account.platform);
                const Icon = platformDef?.icon || Link2;
                return (
                  <div key={account.id} className="p-4 flex items-center gap-4" data-testid={`account-${account.id}`}>
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${platformDef?.color || '#888'}15` }}>
                      <Icon size={18} style={{ color: platformDef?.color || '#888' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{account.display_name || account.handle}</p>
                      <p className="text-xs text-[--ch-text-secondary]">{platformDef?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[--ch-cyan]">{account.content_count} assets</p>
                      <p className="text-xs text-[--ch-text-secondary]">{account.followers?.toLocaleString()} followers</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle size={14} className="text-[--ch-success]" />
                      <span className="text-xs text-[--ch-success] font-bold">Monitored</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {accounts.length === 0 && (
          <div className="ch-card p-12 text-center" data-testid="vault-empty">
            <AlertCircle size={32} className="text-[--ch-text-secondary] mx-auto mb-3" />
            <h3 className="text-lg font-bold mb-1">No Platforms Connected</h3>
            <p className="text-sm text-[--ch-text-secondary] font-normal max-w-md mx-auto">
              Connect your social media accounts above to start indexing your content. SMITHERS will begin monitoring for unauthorized use.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
